<template>
  <div class="min-h-screen bg-white text-gray-900 font-sans selection:bg-[#0d47a1] selection:text-white">
    <!-- Header -->
    <header class="max-w-7xl mx-auto px-8 h-24 flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <div class="h-8 w-8 bg-[#0d47a1] rounded flex items-center justify-center text-white">
          <i class="material-icons text-lg">calculate</i>
        </div>
        <span class="text-lg font-black uppercase tracking-tighter">DIYPayroll</span>
      </div>
      <div class="flex items-center space-x-6">
        <template v-if="is_authenticated">
          <router-link :to="dashboard_link" class="text-xs font-black uppercase tracking-widest text-[#0d47a1] transition-colors">Dashboard</router-link>
          <button @click="logout" class="bg-gray-100 text-gray-500 text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-lg hover:bg-gray-200 transition">Logout</button>
        </template>
        <template v-else>
          <router-link to="/login" class="text-xs font-black uppercase tracking-widest text-gray-500 hover:text-[#0d47a1] transition-colors">Login</router-link>
          <router-link to="/register" class="bg-[#0d47a1] text-white text-xs font-black uppercase tracking-widest px-6 py-2.5 rounded-lg hover:bg-blue-800 transition shadow-sm">Get Started</router-link>
        </template>
      </div>
    </header>

    <!-- Content -->
    <main class="max-w-7xl mx-auto px-8 pt-20 pb-32 relative">
      <DoodleBackground />

      <!-- Straight Glassy Metric Card (Bluer & More Transparent) -->
      <div class="absolute top-10 right-10 lg:right-20 p-8 bg-blue-200/30 backdrop-blur-2xl rounded-3xl border border-blue-200/40 shadow-2xl z-20 animate__animated animate__fadeIn">
        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#0d47a1]/70 mb-2">Total Volume</p>
        <span class="text-4xl font-black text-[#0d47a1] tracking-tighter">$500K+</span>
        <p class="text-[10px] font-bold text-[#0d47a1]/60 uppercase mt-2 border-t border-blue-300/30 pt-2">USD Processed</p>
      </div>

      <div class="max-w-3xl animate__animated animate__fadeIn relative z-10">
        <h1 class="text-6xl font-black leading-[1.05] tracking-tight mb-8">
          The professional environment for <span class="text-[#0d47a1]">organizational payroll.</span>
        </h1>
        <p class="text-xl text-gray-500 font-medium leading-relaxed mb-12">
          A secure workspace for managing employee pay, tax compliance, and financial reporting without the unnecessary complexity.
        </p>
        <router-link to="/register" class="inline-block px-10 py-4 bg-[#0d47a1] text-white text-sm font-black uppercase tracking-widest rounded-lg shadow-lg hover:bg-blue-800 transition transform hover:-translate-y-0.5 active:translate-y-0">Create Your Free Account</router-link>
      </div>

      <!-- Functional Overview -->
      <div class="mt-32 grid grid-cols-1 lg:grid-cols-2 gap-20 border-t border-gray-100 pt-20 relative z-10">
        <div>
          <h2 class="text-xs font-black text-[#0d47a1] uppercase tracking-[0.3em] mb-10">How it works</h2>
          <div class="space-y-12">
            <div>
              <h3 class="text-lg font-black uppercase tracking-tight mb-2">Internal Ledger Controls</h3>
              <p class="text-sm text-gray-500 leading-relaxed">Map payroll categories directly to your Chart of Accounts. Use template strings to automatically create sub-accounts for each employee.</p>
            </div>
            <div>
              <h3 class="text-lg font-black uppercase tracking-tight mb-2">Automated Tax Withholding</h3>
              <p class="text-sm text-gray-500 leading-relaxed">Taxes are calculated automatically based on where you work. Manage state rates and employer costs in one place.</p>
            </div>
            <div>
              <h3 class="text-lg font-black uppercase tracking-tight mb-2">Employee Records</h3>
              <p class="text-sm text-gray-500 leading-relaxed">Keep a complete record for every employee. Get instant access to past paystubs, yearly earnings, and timesheet logs.</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-3xl border border-gray-100 shadow-sm p-2 overflow-hidden">
          <div class="bg-gray-50 rounded-2xl h-full w-full min-h-[400px] flex items-center justify-center p-12">
            <div class="w-full space-y-6 opacity-40 grayscale">
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
              <div class="grid grid-cols-3 gap-4">
                <div class="h-24 bg-gray-200 rounded-xl"></div>
                <div class="h-24 bg-gray-200 rounded-xl"></div>
                <div class="h-24 bg-gray-200 rounded-xl"></div>
              </div>
              <div class="space-y-3">
                <div class="h-2 bg-gray-200 rounded w-full"></div>
                <div class="h-2 bg-gray-200 rounded w-5/6"></div>
                <div class="h-2 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="max-w-7xl mx-auto px-8 py-12 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between text-[10px] font-black uppercase tracking-widest text-gray-400">
      <div class="flex items-center space-x-2 text-gray-900">
        <div class="h-4 w-4 bg-[#0d47a1] rounded flex items-center justify-center text-white"><i class="material-icons text-[10px]">calculate</i></div>
        <span>DIYPayroll © 2026</span>
      </div>
      <div class="flex space-x-8 mt-6 md:mt-0">
        <router-link to="/docs" class="hover:text-[#0d47a1]">Documentation</router-link>
        <router-link to="/privacy" class="hover:text-[#0d47a1]">Privacy</router-link>
        <router-link to="/terms" class="hover:text-[#0d47a1]">Internal Terms</router-link>
      </div>
    </footer>
  </div>
</template>

<script>
import get_api from "../service/api.js"
import base_url from "../service/base_url.js"
import DoodleBackground from '../components/DoodleBackground.vue'

export default {
  name: 'LandingPage',
  components: {
    DoodleBackground
  },
  data() {
    return {
      is_authenticated: !!localStorage.getItem('XPAYROLLaccess_token'),
      dashboard_link: '/login'
    }
  },
  created() {
    if (this.is_authenticated) {
      const api = get_api(this.$router)
      api.get(base_url + "/me").then((resp) => {
        const user = resp.data.data
        if (user.is_admin) {
          this.dashboard_link = "/dashboard"
        } else {
          this.dashboard_link = "/employee/" + user.id
        }
      }).catch(() => {
        this.is_authenticated = false
        localStorage.removeItem('XPAYROLLaccess_token')
      })
    }
  },
  methods: {
    logout() {
      localStorage.removeItem('XPAYROLLaccess_token')
      this.is_authenticated = false
      window.location.reload()
    }
  }
}
</script>

<style scoped>
.animate__animated.animate__fadeIn {
  --animate-duration: 1s;
}
</style>